import React, { useState, useEffect, useRef } from "react";
import { Box, Typography, IconButton, Grid, Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import SettingsSection from "./SettingsSection";
import UnsyncedLeagueName from "./UnsyncedLeagueName";
import { isMobileWebBrowser, mobile } from "src/utils/platformUtils";
import { useAuth0 } from "@auth0/auth0-react";
import { checkMaliciousNotes } from "src/api/checkMaliciousNotes";
import MaliciuosDialog from "../MaliciuosDialog/MaliciuosDialog";
import { extractKeeperData, injectKeeperData } from "src/utils/keeperNotes.utils";
import { KeeperFormData } from "../KeeperDrawer/KeeperDrawer";

interface LeagueSettingsProps {
  onClose: () => void;
  isOpen?: boolean;
  commonLeagueData?: any;
  onSaveSuccess?: () => void;
  onSaveSettings?: (calculatorSettings: any) => void;
  onResetSettings?: () => void;
  calculatorSettings?: any;
  onOpenKeeperDrawer?: () => void;
  /** Un-Synced only: current local league display name + rename handler (league_id unchanged). */
  unsyncedLeagueName?: string | null;
  onRenameUnsyncedLeague?: (newName: string) => Promise<void>;
}

const LeagueSettings: React.FC<LeagueSettingsProps> = ({
  onClose,
  isOpen = true,
  commonLeagueData,
  onSaveSuccess,
  onSaveSettings,
  onResetSettings,
  calculatorSettings,
  onOpenKeeperDrawer,
  unsyncedLeagueName,
  onRenameUnsyncedLeague,
}) => {
  const isMobile = isMobileWebBrowser() || mobile;
  const { getAccessTokenSilently } = useAuth0();

  // Consolidated state
  const [settings, setSettings] = useState({
    leagueType: "Dynasty", //default value
    baseFormat: "STANDARD",
    scoringSystem: "PPR",
    leagueSize: "12",
    // tieBreakers: "1",
    bonusSettings: {
      tePrem: false,
      rbPpc: false,
    },
    notes: "",
  });

  // Initial values to compare against (now state to update from props)
  const [initialValues, setInitialValues] = useState({
    leagueType: "Dynasty",
    baseFormat: "STANDARD",
    scoringSystem: "PPR",
    leagueSize: "12",
    // tieBreakers: "1",
    bonusSettings: {
      tePrem: false,
      rbPpc: false,
    },
    notes: "",
  });

  const [hasChanges, setHasChanges] = useState<boolean>(false); //state to check if there are changes in the settings
  const [showUnsavedDialog, setShowUnsavedDialog] = useState<boolean>(false); //state to show the unsaved changes dialog
  const [showMaliciousDialog, setShowMaliciousDialog] = useState<boolean>(false); //state to show the malicious content dialog
  const [isNotesMalicious, setIsNotesMalicious] = useState<boolean>(false); //state to check if the notes are malicious
  const [isValidatingNotes, setIsValidatingNotes] = useState<boolean>(false); //state to check if the notes are being validated for loader
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const preservedKeeperDataRef = useRef<KeeperFormData | null>(null);

  // Un-Synced only: rename UI is gated by props from LeagueSelector
  const canRenameUnsynced = Boolean(unsyncedLeagueName != null && onRenameUnsyncedLeague);

  // Configuration arrays for sections
  const leagueTypeOptions = ["Dynasty", "Redraft", "Keeper"];
  const baseFormatOptions = ["STANDARD", "SF", "2-QB"];
  const scoringSystemOptions = ["PPR", ".5 PPR", "NON-PPR"];
  const leagueSizeOptions = ["10", "12", "14", "16"];
  // const tieBreakersOptions = ["1", "2", "3"];
  const bonusSettingsOptions = [
    { label: "TE PREM", key: "tePrem" },
    { label: "RB PPC", key: "rbPpc" },
  ];

  // Update settings from commonLeagueData when available
  useEffect(() => {
    if (commonLeagueData && commonLeagueData.league_info) {
      //info is the league info from the common league data
      const info = commonLeagueData.league_info;
      //apiSettings is the settings from the league info
      const apiSettings = info.settings || {};

      // console.log("LEAGUE SETTINGS commonLeagueData", commonLeagueData.league_info);
      // console.log("LEAGUE SETTINGS apiSettings", apiSettings);

      // Map League Type
      let leagueType = "Dynasty"; // default
      if (info.league_type === "redraft" || info.league_type === "Redraft") leagueType = "Redraft";
      else if (info.league_type === "keeper" || info.league_type === "Keeper") leagueType = "Keeper";
      // else dynasty is default (if league_type === "dynasty" or any other value)

      // Map Base Format
      let baseFormat = "STANDARD";
      if (apiSettings.superflex === 1 || apiSettings.superflex === true) baseFormat = "SF";
      else if (apiSettings["2QB"] === 1 || apiSettings["2QB"] === true) baseFormat = "2-QB";
      // else standard is default

      // Map Scoring System
      let scoringSystem = "PPR"; // default
      if (info.league_format === "half_ppr" || info.league_format === ".5 PPR") {
        scoringSystem = ".5 PPR";
      } else if (info.league_format === "nonppr" || info.league_format === "non-ppr" || info.league_format === "Non-PPR" || info.league_format === "NON-PPR") {
        scoringSystem = "NON-PPR";
      }
      // else ppr is default (if league_format === "ppr" or "PPR" or any other value)

      // Map Tie Breakers (from API if available, else default "1")
      // const tieBreakers = (apiSettings.tie_breakers != null && ["1", "2", "3"].includes(String(apiSettings.tie_breakers)))
      //   ? String(apiSettings.tie_breakers)
      //   : "1";

      const rawNotes = info.notes || "";
      const { cleanNotes, keeperData } = extractKeeperData(rawNotes);
      preservedKeeperDataRef.current = keeperData;

      const newSettings = {
        leagueType,
        baseFormat,
        scoringSystem,
        leagueSize: info.league_size?.toString() || "12",
        // tieBreakers,
        bonusSettings: {
          tePrem: apiSettings.TE_Premium === 1 || apiSettings.TE_Premium === true,
          rbPpc: apiSettings.RB_PPC === 1 || apiSettings.RB_PPC === true,
        },
        notes: cleanNotes, // Map note/notes field from commonLeagueData when available (keeper block stripped for UI)
      };

      setSettings(newSettings);
      setInitialValues(newSettings);
    }
  }, [commonLeagueData]);

  // Check for changes
  useEffect(() => {
    const isLeagueTypeChanged = settings.leagueType !== initialValues.leagueType;
    const isBaseFormatChanged = settings.baseFormat !== initialValues.baseFormat;
    const isScoringSystemChanged = settings.scoringSystem !== initialValues.scoringSystem;
    const isLeagueSizeChanged = settings.leagueSize !== initialValues.leagueSize;
    // const isTieBreakersChanged = settings.tieBreakers !== initialValues.tieBreakers;
    const isTePremChanged = settings.bonusSettings.tePrem !== initialValues.bonusSettings.tePrem;
    const isRbPpcChanged = settings.bonusSettings.rbPpc !== initialValues.bonusSettings.rbPpc;
    const isNotesChanged = settings.notes !== initialValues.notes;

    // setHasChanges(isLeagueTypeChanged || isBaseFormatChanged || isScoringSystemChanged || isLeagueSizeChanged || isTieBreakersChanged || isTePremChanged || isRbPpcChanged || isNotesChanged);
    setHasChanges(isLeagueTypeChanged || isBaseFormatChanged || isScoringSystemChanged || isLeagueSizeChanged || isTePremChanged || isRbPpcChanged || isNotesChanged);
  }, [settings]);

  // Reset textarea height when accordion closes
  useEffect(() => {
    if (!isOpen && textareaRef.current) {
      textareaRef.current.style.height = "120px";
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [isOpen]);

  // function to handle the change of the settings
  const handleSettingChange = (key: string, value: any) => {
    // Truncate notes to 1000 characters
    if (key === "notes" && value.length > 1000) {
      value = value.substring(0, 1000);
    }
    setSettings((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  // function to handle the change of the bonus settings
  const handleBonusChange = (key: string) => {
    setSettings((prev) => ({
      ...prev,
      bonusSettings: {
        ...prev.bonusSettings,
        [key]: !prev.bonusSettings[key],
      },
    }));
  };

  // function to handle the save of the settings
  const handleSave = async () => {
    if (hasChanges) {
      const notesToSave = injectKeeperData(settings.notes, preservedKeeperDataRef.current);

      // CRITICAL: If user entered notes, validate for malicious content BEFORE saving
      if (notesToSave && notesToSave.trim()) {
        try {
          console.log("🔍 [LeagueSettings] Notes detected, checking for malicious content...");

          // Set validating state to show loader and disable buttons
          setIsValidatingNotes(true);

          const accessToken = await getAccessTokenSilently();
          const validationResponse = await checkMaliciousNotes(accessToken, notesToSave);

          // Reset validating state after validation completes
          setIsValidatingNotes(false);

          if (validationResponse.isMalicious === 1) {
            // Content is malicious - DO NOT save
            console.warn("⚠️ [LeagueSettings] Note malicious detected, NOT saving anything");

            // Show malicious dialog and set textfield border to red
            setShowMaliciousDialog(true);
            setIsNotesMalicious(true);

            return; // Exit early, don't save anything
          }

          // If validation passes, reset malicious state
          setIsNotesMalicious(false);

          console.log("✅ [LeagueSettings] Notes validated as safe, proceeding with save");
        } catch (error) {
          console.error("❌ [LeagueSettings] Error validating notes:", error);
          // Reset validating state on error
          setIsValidatingNotes(false);
          // If validation fails, don't save for safety
          console.warn("⚠️ [LeagueSettings] Error en validación, NO se guarda nada por seguridad");
          return;
        }
      }

      // Transform settings to calculator_settings structure (similar to league_settings in analyzeTrade)
      const calculatorSettings = {
        league_type: settings.leagueType.toLowerCase(),
        team_format: settings.baseFormat.toLowerCase() === "sf" ? "sf" : settings.baseFormat.toLowerCase() === "2-qb" ? "2qb" : "standard",
        team_type: settings.scoringSystem === ".5 PPR" ? "half_ppr" : settings.scoringSystem === "NON-PPR" ? "nonppr" : "ppr",
        team_size: settings.leagueSize,
        // tie_breakers: settings.tieBreakers,
        te_premium: settings.bonusSettings.tePrem ? 1 : 0,
        rb_premium: settings.bonusSettings.rbPpc ? 1 : 0,
        notes: notesToSave,
      };

      console.log("Saving calculator_settings:", calculatorSettings);

      // Call onSaveSettings to save the settings and trigger refresh
      if (onSaveSettings) {
        onSaveSettings(calculatorSettings);
      }

      // CRITICAL: Update initialValues with the saved settings so next time we open, changes are detected correctly
      setInitialValues(settings);
      setHasChanges(false);

      // Trigger save success callback to show check icon
      if (onSaveSuccess) {
        onSaveSuccess();
      }

      // Close accordion after 1 seconds
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  };

  // function to handle resetting the settings (refresh WITHOUT calculator_settings)
  const handleResetChanges = () => {
    // if calculatorSettings is provided, reset the settings to default (basically refresh the endpoints without calculator_settings)
    if (calculatorSettings) {
      setHasChanges(false);

      // Call onResetSettings to trigger refresh WITHOUT calculator_settings
      // This will make endpoints NOT send the calculator_settings key
      if (onResetSettings) {
        onResetSettings();
      }

      // Trigger save success callback to show check icon (same as Save Changes)
      if (onSaveSuccess) {
        onSaveSuccess();
      }

      // Close accordion after 1 second
      setTimeout(() => {
        onClose();
      }, 1000);
    }
  };

  const leagueTypeFromCommonLeague = commonLeagueData?.league_info?.league_type;
  const isKeeperLeague =
    typeof leagueTypeFromCommonLeague === "string" && leagueTypeFromCommonLeague.trim().toLowerCase() === "keeper";

  const handleCloseSettingsClick = () => {
    if (hasChanges) setShowUnsavedDialog(true);
    else onClose();
  };

  return (
    <Box
      sx={{
        bgcolor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
        display: "flex",
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
      }}
    >
      {/* Header with title and close button */}
      {/* <Box
        sx={{
          p: 1,
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          alignItems: "center",
          flexShrink: 0,
        }}
      > */}
      {/* <Box
          sx={{
            maxWidth: "700px",
            width: "100%",
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
            pb: 1,
            px: 0,
          }}
        >
          {/* <Typography variant="h4" color={`${PureLightThemeCaaS.colors.alpha.white[100]}`} sx={{ fontSize: "20px", fontFamily: '"Inter", sans-serif' }}>
            League Settings
          </Typography> */}
      {/* <IconButton
            aria-label="close"
            onClick={() => {
              if (hasChanges) {
                setShowUnsavedDialog(true);
              } else {
                onClose();
              }
            }}
            sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}` }}
          >
            <CloseIcon />
          </IconButton>
        </Box> */}
      {/* </Box> */}

      {/* Content area - scroll only here, buttons stay fixed below */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehaviorY: "contain",
          WebkitOverflowScrolling: "touch",
          "&::-webkit-scrollbar": { width: 0, display: "none" },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 3,
            flexShrink: 0,
            width: "100%",
            bgcolor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
            // Cover subpixel gap above sticky bar while scrolling (esp. mobile WebKit)
            "&::before": {
              content: '""',
              position: "absolute",
              top: -6,
              left: 0,
              right: 0,
              height: 6,
              bgcolor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              maxWidth: "700px",
              mx: "auto",
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              px: 0.5,
              py: 0.25,
            }}
          >
            <IconButton
              aria-label="close"
              size="small"
              onClick={handleCloseSettingsClick}
              sx={{ color: `${PureLightThemeCaaS.colors.alpha.white[100]}`, ml: 1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
        <Box sx={{ maxWidth: "700px", margin: "0 auto", p: 1, pt: 0, width: "100%" }}>
          {/* Un-Synced only: rename local league name (persisted via user preferences) */}
          {/* canRenameUnsynced is true if the user is on an unsynced platform and has the permission to rename the league name, render the UnsyncedLeagueName component */}
          {canRenameUnsynced && unsyncedLeagueName != null && onRenameUnsyncedLeague && (
            <UnsyncedLeagueName
              leagueName={unsyncedLeagueName}
              onRename={onRenameUnsyncedLeague}
              isSettingsOpen={isOpen}
            />
          )}

          <SettingsSection
            title="LEAGUE TYPE"
            options={leagueTypeOptions}
            selectedValue={settings.leagueType}
            onSelect={(val) => handleSettingChange("leagueType", val)}
            gridSize={4}
          />
          <SettingsSection
            title="BASE FORMAT"
            options={baseFormatOptions}
            selectedValue={settings.baseFormat}
            onSelect={(val) => handleSettingChange("baseFormat", val)}
            gridSize={4}
          />

          <SettingsSection title="BONUS SETTINGS" options={bonusSettingsOptions} selectedValue={settings.bonusSettings} onSelect={handleBonusChange} gridSize={6} isMulti={true} />

          <SettingsSection
            title="SCORING SYSTEM"
            options={scoringSystemOptions}
            selectedValue={settings.scoringSystem}
            onSelect={(val) => handleSettingChange("scoringSystem", val)}
            gridSize={4}
          />

          <SettingsSection
            title="LEAGUE SIZE"
            options={leagueSizeOptions}
            selectedValue={settings.leagueSize}
            onSelect={(val) => handleSettingChange("leagueSize", val)}
            gridSize={3}
          />

          {/* <SettingsSection
            title="TIE BREAKERS"
            options={tieBreakersOptions}
            selectedValue={settings.tieBreakers}
            onSelect={(val) => handleSettingChange("tieBreakers", val)}
            gridSize={4}
          /> */}

          {/* EDIT KEEPERS — keeper leagues only */}
          {isKeeperLeague && onOpenKeeperDrawer && (
            <Box sx={{ mb: 2, display: "flex", flexDirection: "column", alignItems: "center" }}>
              <Typography
                variant="h6"
                sx={{
                  color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                  fontWeight: "bold",
                  mb: 1.5,
                  textTransform: "uppercase",
                  textAlign: "center",
                  fontFamily: '"Inter", sans-serif',
                }}
              >
                To edit your keeper details, click here
              </Typography>
              <Button
                fullWidth
                onClick={onOpenKeeperDrawer}
                sx={{
                  backgroundColor: PureLightThemeCaaS.colors.alpha.orange[100],
                  color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                  width: "50%",
                  fontWeight: "bold",
                  borderRadius: "4px",
                  border: `1px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                  textTransform: "uppercase",
                  fontFamily: '"Inter", sans-serif',
                  fontSize: "13px",
                  py: 0.75,
                  "&:hover": {
                    backgroundColor: PureLightThemeCaaS.colors.alpha.orange[100],
                  },
                }}
              >
                Edit Keepers
              </Button>
            </Box>
          )}

          {/* LEAGUE NOTES Section */}
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{
                color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                fontWeight: "bold",
                mb: 1.5,
                textTransform: "uppercase",
                textAlign: "center",
                fontFamily: '"Inter", sans-serif',
              }}
            >
              LEAGUE NOTES
            </Typography>
            <TextField
              multiline
              rows={6}
              fullWidth
              inputRef={textareaRef}
              value={settings.notes}
              onChange={(e) => {
                handleSettingChange("notes", e.target.value);
                // Reset malicious state when user starts typing again
                if (isNotesMalicious) {
                  setIsNotesMalicious(false);
                }
              }}
              inputProps={{ maxLength: 1000 }}
              placeholder="Enter league rules, payout details, or general notes..."
              sx={{
                "&::-webkit-scrollbar": {
                  width: "0px",
                  display: "none",
                },
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                "& .MuiOutlinedInput-root": {
                  backgroundColor: PureLightThemeCaaS.colors.alpha.black[50],
                  color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                  borderRadius: "10px",
                  "& fieldset": {
                    borderColor: settings.notes.length >= 1000 || isNotesMalicious ? "#d32f2f" : `${PureLightThemeCaaS.colors.alpha.white[30]}`,
                  },
                  "&:hover fieldset": {
                    borderColor: settings.notes.length >= 1000 || isNotesMalicious ? "#d32f2f" : `${PureLightThemeCaaS.colors.alpha.white[50]}`,
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: settings.notes.length >= 1000 || isNotesMalicious ? "#d32f2f" : `${PureLightThemeCaaS.colors.alpha.orange[100]}`,
                    borderWidth: "1px",
                  },
                },
                "& .MuiInputBase-input": {
                  color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
                  fontFamily: '"Inter", sans-serif',
                  "&::placeholder": {
                    color: `${PureLightThemeCaaS.colors.alpha.white[50]}`,
                    opacity: 1,
                    fontFamily: '"Inter", sans-serif',
                  },
                },
                "& textarea": {
                  resize: "vertical",
                  minHeight: "120px",
                  maxHeight: "350px",
                  overflowY: "auto",
                  "&::-webkit-scrollbar": {
                    width: "0px",
                    display: "none",
                  },
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                },
              }}
            />
            {/* Character counter */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                pt: 0.5,
                px: 0.5,
              }}
            >
              <Typography
                sx={{
                  // color: settings.notes.length >= 1000 ? "#d32f2f" : `${PureLightThemeCaaS.colors.alpha.white[70]}`,
                  color: `${PureLightThemeCaaS.colors.alpha.white[70]}`,
                  fontSize: "12px",
                  fontFamily: '"Inter", sans-serif',
                  fontWeight: settings.notes.length >= 1000 ? "bold" : "normal",
                }}
              >
                {settings.notes.length}/1000
              </Typography>
            </Box>
          </Box>

          {/* Spacer before buttons */}
          <Box sx={{ height: 2 }} />
        </Box>
      </Box>

      {/* SAVE CHANGES button - fixed at bottom, never scrolls */}
      <Box
        sx={{
          flexShrink: 0,
          bgcolor: `${PureLightThemeCaaS.colors.alpha.black[100]}`,
          pt: 0.5,
          // pb: "env(safe-area-inset-bottom)",
          pb: 3.5,
        }}
      >
        <Box
          sx={{
            maxWidth: "700px",
            margin: "0 auto",
            p: 1,
            pb: 0, pt: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 0,
          }}
        >
          <Button
            fullWidth
            onClick={handleSave}
            disabled={!hasChanges || isValidatingNotes}
            sx={{
              backgroundColor: hasChanges ? PureLightThemeCaaS.colors.alpha.orange[100] : PureLightThemeCaaS.colors.alpha.black[50],
              color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
              fontWeight: "bold",
              borderRadius: "4px",
              border: `1px solid ${PureLightThemeCaaS.colors.alpha.orange[100]}`,
              textTransform: "uppercase",
              fontFamily: '"Inter", sans-serif',
              fontSize: "12px",
              minHeight: "30px", // Fixed height to prevent button from resizing
              height: "30px", // Fixed height
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              "&:hover": {
                backgroundColor: hasChanges ? PureLightThemeCaaS.colors.alpha.orange[100] : PureLightThemeCaaS.colors.alpha.black[50],
              },
              "&.Mui-disabled": {
                backgroundColor: PureLightThemeCaaS.colors.alpha.black[50],
                color: `${PureLightThemeCaaS.colors.alpha.white[50]}`,
                border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
              },
            }}
          >
            {isValidatingNotes ? <CircularProgress size={25} sx={{ color: "#FF672D" }} /> : "SAVE CHANGES"}
          </Button>
          <Button
            onClick={handleResetChanges}
            disabled={!calculatorSettings || isValidatingNotes}
            size="small"
            variant="text"
            sx={{
              // backgroundColor: hasChanges ? PureLightThemeCaaS.colors.alpha.orange[100] : PureLightThemeCaaS.colors.alpha.black[50],
              backgroundColor: "transparent",
              color: `${PureLightThemeCaaS.colors.alpha.white[100]}`,
              fontWeight: "bold",
              // borderRadius: "4px",
              border: `none`,
              textTransform: "uppercase",
              fontFamily: '"Inter", sans-serif',
              fontSize: "9px",
              // "&:hover": {
              //   backgroundColor: hasChanges ? PureLightThemeCaaS.colors.alpha.orange[100] : PureLightThemeCaaS.colors.alpha.black[50],
              // },
              "&.Mui-disabled": {
                backgroundColor: PureLightThemeCaaS.colors.alpha.black[50],
                color: `${PureLightThemeCaaS.colors.alpha.white[50]}`,
                border: `none`,
              },
            }}
          >
            RESET CHANGES
          </Button>
        </Box>
      </Box>

      {/* Unsaved Changes Dialog */}
      <Dialog
        open={showUnsavedDialog}
        onClose={(event, reason) => {
          // Prevent closing when clicking outside (backdrop) - only allow closing from buttons
          // if (reason === "backdropClick") {
          //   return;
          // }
          setShowUnsavedDialog(false);
        }}
        sx={{
          zIndex: 1600,
        }}
        PaperProps={{
          sx: {
            backgroundColor: PureLightThemeCaaS.colors.alpha.black[100],
            color: PureLightThemeCaaS.colors.alpha.white[100],
            boxShadow: "4px 4px 12px rgba(0,0,0,0.25)",
            borderRadius: "8px",
            border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
          },
        }}
        slotProps={{
          backdrop: {
            sx: {
              position: "fixed",
              backdropFilter: "blur(6px) !important",
              WebkitBackdropFilter: "blur(1px) !important",
              backgroundColor: "rgba(0, 0, 0, 0.35)",
            },
          },
        }}
      >
        <DialogTitle sx={{ color: PureLightThemeCaaS.colors.alpha.white[100], fontWeight: "bold", fontSize: "20px", fontFamily: '"Inter", sans-serif' }}>
          Unsaved Changes
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: PureLightThemeCaaS.colors.alpha.white[70], fontWeight: 500, fontSize: "14px", lineHeight: "1.2", fontFamily: '"Inter", sans-serif' }}>
            You have unsaved changes. Are you sure you want to close without saving?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 1, gap: 1 }}>
          <Button
            onClick={() => setShowUnsavedDialog(false)}
            sx={{
              color: PureLightThemeCaaS.colors.alpha.white[100],
              backgroundColor: PureLightThemeCaaS.colors.alpha.black[50],
              fontFamily: '"Inter", sans-serif',
              "&:hover": {
                backgroundColor: PureLightThemeCaaS.colors.alpha.black[70],
              },
            }}
          >
            Go Back
          </Button>
          <Button
            onClick={() => {
              // Reset settings to initial values before closing
              setSettings(initialValues);
              setHasChanges(false);
              setShowUnsavedDialog(false);
              onClose();
            }}
            sx={{
              color: PureLightThemeCaaS.colors.alpha.white[100],
              backgroundColor: "#d32f2f",
              fontFamily: '"Inter", sans-serif',
              "&:hover": {
                backgroundColor: "#b71c1c",
              },
            }}
          >
            Discard Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Malicious Content Dialog */}
      <MaliciuosDialog open={showMaliciousDialog} onClose={() => setShowMaliciousDialog(false)} autoCloseDelay={5000} />
    </Box>
  );
};

export default LeagueSettings;
